import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useUser,
  useUpdateUser,
  useRoles,
  useCheckUsernameAvailability,
} from '../../services/httpServices/queries';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Save,
  AtSign,
  Phone,
  Lock,
  ToggleLeft,
  ToggleRight,
  Check,
  XCircle,
  Eye,
  EyeOff,
  LayoutDashboard,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ASSIGNABLE_SYSTEM_ROLES } from '../../hooks/usePermissions';

// SUPER_ADMIN is excluded — it is reserved for the master developer and is
// managed from the Developer Console (/settings/developer).
const ALL_SYSTEM_ROLES: string[] = ASSIGNABLE_SYSTEM_ROLES;

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: userData, isLoading: userLoading, error: userError } = useUser(id || '');
  const { data: rolesData } = useRoles();
  const updateUser = useUpdateUser();

  const user = userData?.data;
  const roles = rolesData?.data || [];

  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    roles: [] as string[],
    customRoleIds: [] as string[],
    isActive: true,
    dashboardAccess: false,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const trimmedUsername = formData.username.trim();
  const usernameChanged = isEdit && trimmedUsername.length > 0 && trimmedUsername !== user?.username;
  const { data: usernameCheck, isFetching: checkingUsername } = useCheckUsernameAvailability(
    usernameChanged ? trimmedUsername : '',
    id,
  );
  const usernameAvailable = usernameCheck?.data?.available;

  useEffect(() => {
    if (isEdit && user) {
      setFormData({
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        mobile: user.mobile || '',
        password: '',
        roles: user.roles || [],
        customRoleIds: user.customRoles?.map((r: any) => r.id) || [],
        isActive: user.isActive ?? true,
        dashboardAccess: user.dashboardAccess === true,
      });
    }
  }, [isEdit, user]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (usernameChanged && usernameAvailable === false) errors.username = 'Username already exists';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !id) return;

    const { password, ...rest } = formData;
    const payload = password ? { ...rest, password } : rest;

    try {
      await updateUser.mutateAsync({ id, data: payload });
      navigate(`/users/${id}`);
    } catch {
      // handled by mutation
    }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load user. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 max-w-3xl mx-auto">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/users/${id}`)}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to User
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-text">Edit User</h1>
        <span className="text-text-muted text-sm">{user.firstName} {user.lastName}</span>
      </div>

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Basic Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Username *</label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  required
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className={cn(
                    'w-full pl-9 pr-9 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
                    formErrors.username || (usernameChanged && usernameAvailable === false)
                      ? 'border-danger'
                      : 'border-border'
                  )}
                />
                {usernameChanged && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingUsername ? (
                      <Loader2 size={16} className="animate-spin text-text-muted" />
                    ) : usernameAvailable === false ? (
                      <XCircle size={16} className="text-danger" />
                    ) : usernameAvailable === true ? (
                      <Check size={16} className="text-success" />
                    ) : null}
                  </span>
                )}
              </div>
              {formErrors.username ? (
                <p className="text-xs text-danger mt-1">{formErrors.username}</p>
              ) : usernameChanged && usernameAvailable === true ? (
                <p className="text-xs text-success mt-1">Username is available</p>
              ) : usernameChanged && checkingUsername ? (
                <p className="text-xs text-text-muted mt-1">Checking availability...</p>
              ) : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Email *</label>
              <input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
                  formErrors.email ? 'border-danger' : 'border-border'
                )}
              />
              {formErrors.email && <p className="text-xs text-danger mt-1">{formErrors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">First Name *</label>
              <input
                required
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
                  formErrors.firstName ? 'border-danger' : 'border-border'
                )}
              />
              {formErrors.firstName && <p className="text-xs text-danger mt-1">{formErrors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Last Name *</label>
              <input
                required
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary',
                  formErrors.lastName ? 'border-danger' : 'border-border'
                )}
              />
              {formErrors.lastName && <p className="text-xs text-danger mt-1">{formErrors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">Mobile</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">New Password (leave blank to keep current)</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-9 pr-10 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>

        {/* System Roles */}
        <div className="space-y-4 pt-4 border-t border-border">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">System Roles</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-border rounded-lg bg-background max-h-52 overflow-y-auto">
            {ALL_SYSTEM_ROLES.map((role) => (
              <label key={role} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.roles.includes(role)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, roles: [...formData.roles, role] });
                    } else {
                      setFormData({ ...formData, roles: formData.roles.filter((r) => r !== role) });
                    }
                  }}
                  className="rounded border-border"
                />
                <span className="text-sm text-text">{getRoleLabel(role)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Roles */}
        <div className="space-y-4 pt-4 border-t border-border">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Custom Roles</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-border rounded-lg bg-background max-h-52 overflow-y-auto">
            {roles.length === 0 ? (
              <span className="text-sm text-text-muted col-span-full">No custom roles available</span>
            ) : (
              roles.map((role: any) => (
                <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.customRoleIds.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, customRoleIds: [...formData.customRoleIds, role.id] });
                      } else {
                        setFormData({ ...formData, customRoleIds: formData.customRoleIds.filter((id) => id !== role.id) });
                      }
                    }}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-text">{role.name}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Status */}
        <div className="space-y-4 pt-4 border-t border-border">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Status</h2>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
            className="flex items-center gap-2 text-sm text-text"
          >
            {formData.isActive ? <ToggleRight className="text-success" size={20} /> : <ToggleLeft className="text-text-muted" size={20} />}
            <span>{formData.isActive ? 'Active' : 'Inactive'}</span>
          </button>
        </div>

        {/* Dashboard access */}
        <div className="space-y-4 pt-4 border-t border-border">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-text-muted uppercase tracking-wider">
            <LayoutDashboard size={15} />
            Dashboard Access
          </h2>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text">
                {formData.dashboardAccess ? 'Access Allowed' : 'Access Denied'}
              </p>
              <p className="text-xs text-text-muted">
                {formData.dashboardAccess
                  ? 'This user can sign in to the dashboard.'
                  : 'This user cannot sign in to the dashboard.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={formData.dashboardAccess}
              onClick={() => setFormData({ ...formData, dashboardAccess: !formData.dashboardAccess })}
              className={cn(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                formData.dashboardAccess ? 'bg-success' : 'bg-border',
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
                  formData.dashboardAccess ? 'translate-x-[24px]' : 'translate-x-[4px]',
                )}
              />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate(`/users/${id}`)}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateUser.isPending || (usernameChanged && (checkingUsername || usernameAvailable === false))}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {updateUser.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}
